import type { QuotaLimitKind, QuotaStatus } from "@szyos/contracts";
import { PROVIDER_ADAPTERS } from "./provider-auth";

/** Token-Verbrauch einer Menge von Läufen. `cacheReadTokens` ist ein Teil von `inputTokens`. */
export interface TokenTally {
  runs: number;
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
}

export const NO_TOKENS: TokenTally = { runs: 0, inputTokens: 0, outputTokens: 0, cacheReadTokens: 0 };

export function addTally(a: TokenTally, b: TokenTally): TokenTally {
  return {
    runs: a.runs + b.runs,
    inputTokens: a.inputTokens + b.inputTokens,
    outputTokens: a.outputTokens + b.outputTokens,
    cacheReadTokens: a.cacheReadTokens + b.cacheReadTokens,
  };
}

/**
 * Neu verarbeitete Tokens: Eingabe ohne Cache-Lesezugriffe plus Ausgabe. Cache-Lesezugriffe zählen beim
 * Anbieter nur zu einem Bruchteil und werden getrennt ausgewiesen.
 */
export function processedTokens(t: TokenTally): number {
  return Math.max(0, t.inputTokens - t.cacheReadTokens) + t.outputTokens;
}

/** Nur Läufe über einen echten Anbieter verbrauchen Tokens; Mock-Läufe sind Simulation und zählen nie mit. */
export function countsAsRealUsage(adapter: string): boolean {
  return PROVIDER_ADAPTERS.has(adapter);
}

export const WEEK_MS = 7 * 86_400_000;

/** Zeitraum der aktuellen Woche: aus der Rücksetzzeit des Wochenlimits (`abo`) oder Kalenderwoche ab Montag. */
export interface UsageWindow {
  from: string;
  to: string;
  source: "abo" | "kalender";
}

/**
 * Die Woche, die das Abo zählt, endet mit der gemeldeten Rücksetzzeit des Wochenlimits. Liegt sie in der
 * Vergangenheit, rückt sie in Wochenschritten vor. Ohne gültige Meldung gilt die Kalenderwoche
 * (Montag 0 Uhr in `zone`).
 */
export function usageWeek(now: Date, weeklyResetsAt: string | null, zone = "Europe/Berlin"): UsageWindow {
  const reset = weeklyResetsAt ? Date.parse(weeklyResetsAt) : Number.NaN;
  if (Number.isFinite(reset) && reset - now.getTime() <= WEEK_MS) {
    let to = reset;
    if (to <= now.getTime()) to += Math.floor((now.getTime() - to) / WEEK_MS + 1) * WEEK_MS;
    return { from: new Date(to - WEEK_MS).toISOString(), to: new Date(to).toISOString(), source: "abo" };
  }
  const w = wallClock(now, zone);
  const sinceMonday = (w.weekday + 6) % 7;
  const from = zonedToUtc(w.year, w.month, w.day - sinceMonday, zone);
  const to = zonedToUtc(w.year, w.month, w.day - sinceMonday + 7, zone);
  return { from: from.toISOString(), to: to.toISOString(), source: "kalender" };
}

/** Zuletzt gemeldeter Limitstand je Limitart. */
export interface QuotaObservation {
  limit: QuotaLimitKind;
  status: QuotaStatus;
  utilization: number | null;
  resetsAt: string | null;
  observedAt: string;
}

export interface QuotaView extends QuotaObservation {
  /** `reset`: Die gemeldete Rücksetzzeit ist vorbei, der alte Stand gilt nicht mehr. */
  state: "ok" | "warning" | "exhausted" | "reset";
  /** Freier Anteil (0 bis 1), nur wenn der Anbieter einen Anteil gemeldet hat; sonst `null`. */
  remainingShare: number | null;
}

const LIMIT_ORDER: QuotaLimitKind[] = ["session", "weekly", "model", "extra_usage", "unknown"];

/** Bewertet gemeldete Limitstände zum Zeitpunkt `now`, ohne fehlende Anteile zu schätzen. */
export function quotaViews(observations: QuotaObservation[], now: Date): QuotaView[] {
  return [...observations]
    .sort((a, b) => LIMIT_ORDER.indexOf(a.limit) - LIMIT_ORDER.indexOf(b.limit))
    .map((o) => {
      const reset = o.resetsAt ? Date.parse(o.resetsAt) : Number.NaN;
      if (Number.isFinite(reset) && reset <= now.getTime()) {
        return { ...o, state: "reset", remainingShare: null };
      }
      if (o.status === "rejected") return { ...o, state: "exhausted", remainingShare: 0 };
      return {
        ...o,
        state: o.status === "allowed_warning" ? "warning" : "ok",
        remainingShare: o.utilization === null ? null : Math.max(0, 1 - o.utilization),
      };
    });
}

interface WallDate {
  year: number;
  month: number;
  day: number;
  /** 0 = Sonntag wie bei `Date.getUTCDay`. */
  weekday: number;
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function wallClock(at: Date, zone: string): WallDate {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: zone,
    year: "numeric",
    month: "numeric",
    day: "numeric",
    weekday: "short",
  }).formatToParts(at);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return {
    year: Number(get("year")),
    month: Number(get("month")),
    day: Number(get("day")),
    weekday: WEEKDAYS.indexOf(get("weekday")),
  };
}

/** Mitternacht eines (auch überlaufenden) Kalendertags in `zone` als UTC-Zeitpunkt. */
function zonedToUtc(year: number, month: number, day: number, zone: string): Date {
  const target = Date.UTC(year, month - 1, day);
  let guess = target;
  for (let i = 0; i < 2; i += 1) {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone: zone,
      hourCycle: "h23",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
    }).formatToParts(new Date(guess));
    const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
    const shown = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"));
    guess += target - shown;
  }
  return new Date(guess);
}

/** Verbrauch eines Bereichs in der aktuellen Woche und insgesamt. */
export interface UsageBucket {
  week: TokenTally;
  total: TokenTally;
}

export interface WorkOrderUsage extends UsageBucket {
  workOrderId: string;
  title: string;
  state: string;
  /** Planvorschlag im Planungsstudio für diesen Auftrag. */
  planning: TokenTally;
  items: { workItemId: string; title: string; total: TokenTally }[];
}

export interface ProjectUsage extends UsageBucket {
  projectId: string;
  title: string;
  /** Klärung der Idee im Planungsstudio, bevor sie zum Projekt wurde. */
  planning: TokenTally;
  workOrders: WorkOrderUsage[];
}

export interface UsageOverview extends UsageBucket {
  window: UsageWindow;
  projects: ProjectUsage[];
  /** Klärungen von Ideen, die noch kein Projekt sind. */
  ideas: UsageBucket;
  /** Mock-Läufe (Demo, Tests): zählen nie zum Verbrauch, nur zur Einordnung. */
  simulated: UsageBucket;
  limits: QuotaView[];
}

/** Ein Lauf mit seiner Zuordnung; `projectId` fehlt bei Klärungen noch nicht übernommener Ideen. */
export interface UsageRun {
  adapter: string;
  at: string;
  projectId: string | null;
  workOrderId: string | null;
  workItemId: string | null;
  purpose: "work" | "studio";
  inputTokens: number;
  outputTokens: number;
  cacheReadTokens: number;
}

export interface UsageInput {
  runs: UsageRun[];
  projects: { id: string; title: string }[];
  workOrders: { id: string; projectId: string; title: string; state: string }[];
  items: { id: string; workOrderId: string; title: string }[];
  window: UsageWindow;
  limits: QuotaView[];
}

function tallyOf(run: UsageRun): TokenTally {
  return {
    runs: 1,
    inputTokens: run.inputTokens,
    outputTokens: run.outputTokens,
    cacheReadTokens: Math.min(run.cacheReadTokens, run.inputTokens),
  };
}

/** Summiert echten Verbrauch je Projekt, Auftrag und Aufgabe, für die aktuelle Woche und insgesamt. */
export function summarizeUsage(input: UsageInput): UsageOverview {
  const from = Date.parse(input.window.from);
  const to = Date.parse(input.window.to);
  const inWeek = (run: UsageRun) => {
    const t = Date.parse(run.at);
    return t >= from && t < to;
  };
  const bucket = (runs: UsageRun[]): UsageBucket => ({
    week: runs.filter(inWeek).map(tallyOf).reduce(addTally, NO_TOKENS),
    total: runs.map(tallyOf).reduce(addTally, NO_TOKENS),
  });
  const sum = (runs: UsageRun[]) => runs.map(tallyOf).reduce(addTally, NO_TOKENS);
  const real = input.runs.filter((r) => countsAsRealUsage(r.adapter));
  const simulated = input.runs.filter((r) => !countsAsRealUsage(r.adapter));
  const projectIds = new Set(input.projects.map((p) => p.id));

  const projects = input.projects.map((p): ProjectUsage => {
    const ofProject = real.filter((r) => r.projectId === p.id);
    const workOrders = input.workOrders
      .filter((w) => w.projectId === p.id)
      .map((w): WorkOrderUsage => {
        const ofOrder = ofProject.filter((r) => r.workOrderId === w.id);
        return {
          workOrderId: w.id,
          title: w.title,
          state: w.state,
          ...bucket(ofOrder),
          planning: sum(ofOrder.filter((r) => r.purpose === "studio")),
          items: input.items
            .filter((i) => i.workOrderId === w.id)
            .map((i) => ({
              workItemId: i.id,
              title: i.title,
              total: sum(ofOrder.filter((r) => r.workItemId === i.id)),
            })),
        };
      });
    return {
      projectId: p.id,
      title: p.title,
      ...bucket(ofProject),
      planning: sum(ofProject.filter((r) => r.purpose === "studio" && r.workOrderId === null)),
      workOrders,
    };
  });

  return {
    window: input.window,
    ...bucket(real),
    projects,
    ideas: bucket(real.filter((r) => r.projectId === null || !projectIds.has(r.projectId))),
    simulated: bucket(simulated),
    limits: input.limits,
  };
}
